import axios from 'axios'
import studentRequest from 'utils/studentRequest'
import { filenameForMimeType } from 'utils/audioFormat'

const FASTAPI = process.env.REACT_APP_FASTAPI_URL || ''

const authHeader = () => {
  const token =
    (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('token')) ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('token')) ||
    ''
  return token ? { Authorization: token } : {}
}

export const startSession = (assessmentAssignedId, resumeText) =>
  studentRequest.post('/ai-interview/session/start', { assessmentAssignedId, resumeText })

// deliverySignals (wpm/disfluency/etc.) is only populated when the client's own
// read-aloud heuristic suspected this answer — the backend runs an LLM check on
// it before the question can be shown as flagged for reading in any report.
export const submitTurn = (sessionId, interactionId, answerText, resumeText, deliverySignals) =>
  studentRequest.post('/ai-interview/session/turn', { sessionId, interactionId, answerText, resumeText, deliverySignals })

// `completionReason` is how the interview ended on this client — 'time_up' when
// the countdown expired, 'candidate_initiated' when they pressed End,
// 'proctoring_violation' when the fullscreen rule closed it. Without it the
// server can only assume the candidate walked out, which is what the report
// then tells the recruiter. Server-side it is re-checked against the session
// clock, so it is a signal, not a verdict.
export const completeSession = (sessionId, candidateName, assessmentAssignedId, completionReason) =>
  studentRequest.post('/ai-interview/session/complete', {
    sessionId,
    candidateName,
    assessmentAssignedId,
    completionReason,
  })

export const fetchReport = (sessionId) =>
  studentRequest.get(`/ai-interview/report/${sessionId}`)

// Read-only metadata for the instructions screen. Doesn't create a session.
export const fetchConfigInfo = (assessmentAssignedId) =>
  studentRequest.get(`/ai-interview/config-info/${assessmentAssignedId}`)

// FastAPI direct calls — voice.
// `voice` is the admin-selected ElevenLabs voice_id for this interview. When
// null, the backend falls back to its default voice. (A non-ElevenLabs value
// like 'aura-*' routes to the Deepgram fallback only if no ElevenLabs key.)
//
// `language` is the language of THIS line and `languages` is every language the
// interview was configured with. Both matter and they are not interchangeable:
// `languages` picks the provider once for the whole sitting so the voice never
// changes mid-interview, while `language` tells that provider what to speak.
// Sending neither — which this call did until 2026-09-22 — left the backend
// sniffing the text for Devanagari, so a Kannada or Tamil question was labelled
// English and read by an English voice.
export const tts = async (text, voice = null, language = null, languages = null) => {
  const resp = await axios.post(
    `${FASTAPI}/ai-interview/tts`,
    {
      text,
      voice: voice || undefined,
      language: language || undefined,
      languages: languages && languages.length ? languages : undefined,
    },
    { headers: authHeader(), responseType: 'blob', timeout: 60000 }
  )
  return resp.data
}

export const stt = async (audioBlob, language = 'en') => {
  const fd = new FormData()
  // Name the part by the blob's actual container (iOS records mp4, not webm) so
  // the backend forwards a correct Content-Type to Deepgram.
  fd.append('audio', audioBlob, filenameForMimeType('answer', audioBlob && audioBlob.type))
  fd.append('language', language)
  const resp = await axios.post(
    `${FASTAPI}/ai-interview/stt`,
    fd,
    { headers: authHeader(), timeout: 60000 }
  )
  return resp.data
}

// Proctoring-only: re-derives the read-aloud numeric signals (pacingCv/
// avgWordConfidence/fillerCount/pauseRate) from a turn's audio via Deepgram —
// Sarvam (the transcript source) can't provide these. Transcript is discarded;
// only used for the delivery-signal shape analyzeAnswerDelivery expects.
export const deliveryTelemetry = async (audioBlob, language = 'en') => {
  const fd = new FormData()
  fd.append('audio', audioBlob, filenameForMimeType('answer', audioBlob && audioBlob.type))
  fd.append('language', language)
  const resp = await axios.post(
    `${FASTAPI}/ai-interview/delivery-telemetry`,
    fd,
    { headers: authHeader(), timeout: 60000 }
  )
  return resp.data
}

// Backfills the real delivery signals onto an already-submitted turn once
// deliveryTelemetry resolves — fire-and-forget from the caller.
export const backfillDeliveryTelemetry = (sessionId, interactionId, deliverySignals) =>
  studentRequest.post('/ai-interview/session/turn/delivery-telemetry', { sessionId, interactionId, deliverySignals })

// Upload the full-session mixed recording (interviewer voice + candidate
// answers, mixed by sessionRecorder.js) so the admin Student Report's
// existing "Audio" button — which matches student_answers.object_key
// filenames containing "audio" — picks it up for AI Interview too. Reuses
// the SAME upload endpoint/table Communication assessments already use;
// fire-and-forget from the caller's perspective (failure must never block
// interview submission).
export const uploadSessionAudio = async (assessmentAssignedId, audioBlob, mimeType, chapters, extraConfig) => {
  const fd = new FormData()
  const ext = filenameForMimeType('x', mimeType || (audioBlob && audioBlob.type)).split('.').pop()
  // Must contain "audio" (case-insensitive) — that's the pattern the admin
  // report's getMediaKeys() matches against student_answers.object_key.
  //
  // Deliberately STABLE per assignment (no Date.now()): the periodic flush
  // re-uploads a longer cumulative recording every 20s, and each write must
  // OVERWRITE the previous object rather than leave a trail of partial
  // orphans in object storage. The student_answers row already upserts on
  // {assessmentAssignedId, questionId}, so the key matches the row 1:1 and
  // the last (longest) write wins.
  const filename = `AI_Interview_audio_${assessmentAssignedId}.${ext}`
  // Field order matters for Fastify multipart — fields before the file.
  // questionId must be a real UUID — student_answers.question_id is a
  // uuid NOT NULL FK into assessment.questions. A placeholder row (fixed ID,
  // is_active=false) is seeded in every env for exactly this purpose; a
  // non-UUID string here fails the insert silently server-side (caught as
  // "non-critical") while Oracle upload still succeeds, so the recording
  // exists but never becomes visible in the admin report.
  fd.append('assessmentAssignedId', assessmentAssignedId)
  fd.append('questionId', 'a55c5db4-a546-41a4-b103-c17401e82b4e')
  // Per-question offsets into this recording, for the admin "jump to
  // question" player. Stashed in student_answers.answer_text — unused for
  // this row otherwise — so no schema change is needed.
  fd.append('chapters', JSON.stringify(chapters || []))
  fd.append('audio', audioBlob, filename)
  // extraConfig lets the durable upload queue (sessionUploadQueue.js) pass an
  // explicit Authorization header snapshot so a retry after a full tab-close
  // (empty sessionStorage) still authenticates.
  return studentRequest.post('/students/assessments/uploadAudio', fd, {
    headers: { 'Content-Type': 'multipart/form-data', ...(extraConfig?.headers || {}) },
    timeout: 120000,
    ...(extraConfig ? (({ headers, ...rest }) => rest)(extraConfig) : {}),
  })
}

// Upload a resume file (PDF/DOCX/TXT) and get back the extracted plain text
// that we can pass through to startSession as resumeText.
export const parseResume = async (file) => {
  const fd = new FormData()
  fd.append('file', file, file.name || 'resume')
  const resp = await axios.post(
    `${FASTAPI}/ai-interview/parse-resume`,
    fd,
    { headers: authHeader(), timeout: 120000 }
  )
  return resp.data
}
